import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const WooContext = createContext(null);

export function WooProvider({ children }) {
  const [map, setMap] = useState({});          // wooId -> product
  const [currency, setCurrency] = useState('USD');
  const [symbol, setSymbol] = useState('$');
  const [minorUnit, setMinorUnit] = useState(2);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/woo/products`)
      .then((r) => {
        if (!alive) return;
        const m = {};
        (r.data.products || []).forEach((p) => { m[p.id] = p; });
        setMap(m);
        const first = (r.data.products || [])[0];
        if (first) { setCurrency(first.currency); setSymbol(first.currency_symbol || '$'); setMinorUnit(first.currency_minor_unit ?? 2); }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { alive = false; };
  }, []);

  // price of a woo product id in major units. Falls back to fixed EUR price
  // (the live WooCommerce is no longer reachable at the-sman.com).
  const FALLBACK_BOX_EUR = 17.0;
  const priceOf = useCallback((wooId) => {
    const p = map[wooId];
    if (!p || p.price_minor == null) return FALLBACK_BOX_EUR;
    return p.price_minor / 10 ** (p.currency_minor_unit ?? 2);
  }, [map]);

  const inStock = useCallback((wooId) => {
    const p = map[wooId];
    return p ? p.is_in_stock : true;
  }, [map]);

  // Display prices in EUR on the site (store base currency may differ).
  // NOTE: the hosted WooCommerce checkout still charges in the store's own currency.
  const DISPLAY_CURRENCY = 'EUR';
  const DISPLAY_LOCALE = 'it-IT';

  const money = useCallback((amount) => {
    if (amount == null || isNaN(amount)) return '—';
    try {
      return new Intl.NumberFormat(DISPLAY_LOCALE, { style: 'currency', currency: DISPLAY_CURRENCY, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    } catch {
      return `${Number(amount).toFixed(2)} €`;
    }
  }, []);

  // Stripe Checkout: cartItems -> hosted Stripe payment page
  const checkout = useCallback(async (cartItems) => {
    const items = (cartItems || [])
      .filter((i) => i && i.qty > 0 && (i.boxes || 0) > 0)
      .map((i) => ({ title: i.title, boxes: i.boxes, qty: i.qty }));
    if (!items.length) return false;
    const r = await axios.post(`${API}/api/payments/checkout`, { items, origin_url: window.location.origin });
    if (r.data && r.data.checkout_url) { window.location.assign(r.data.checkout_url); return true; }
    return false;
  }, []);

  return (
    <WooContext.Provider value={{ map, loaded, currency, symbol, minorUnit, priceOf, inStock, money, checkout }}>
      {children}
    </WooContext.Provider>
  );
}

export const useWoo = () => useContext(WooContext);
